import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    Button,
    IconButton,
    Typography,
    Box,
    Avatar,
    Collapse,
    Alert,
    Divider,
    CircularProgress
} from '@mui/material';

import {
    PiIdentificationCardLight as NameCardIcon
} from 'react-icons/pi';

import { IoClose as Close } from 'react-icons/io5';

import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import {
    getCountries,
    getStates
} from 'country-state-picker';
import PhoneInput, {
    isValidPhoneNumber,
    isPossiblePhoneNumber
} from 'react-phone-number-input';
import {
    parsePhoneNumberFromString
} from 'libphonenumber-js';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import ErrorHandler from '@Root/ErrorHandler';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import 'react-phone-number-input/style.css';
import './NameCardForm.scss';

export default function NameCardForm(props) {

    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [designation, setDesignation] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [organization, setOrganization] = useState(null);
    const [organizationCardList, setOrganizationCardList] = useState([]);
    const [photo, setPhoto] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [uploadStatus, setUploadStatus] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [department, setDepartment] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [address, setAddress] = useState('');
    const [pinCode, setPinCode] = useState('');
    const [userState, setUserState] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [states, setStates] = useState([]);
    const [userCountry, setUserCountry] = useState(null);
    const [country, setCountry] = useState('');
    const [remarks, setRemarks] = useState('');
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isPossibleNumber, setIsPossibleNumber] = useState(false);
    const [isValidNumber, setIsValidNumber] = useState(false);
    const countries = getCountries();
    const errorHandler = new ErrorHandler();
    const nameCardService = new NameCardService();
    const { currentProjectId } = useReduxProject();
    const className = 'nameCardForm';

    const sanitize = (value) => DOMPurify.sanitize(value);

    const emailValidCheck = (value) => {
        const validRegex =
            /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;

        return Boolean(value && value.match(validRegex));
    };

    const getInitials = (value = '') => {
        const words = value.trim().split(/\s+/);

        if (!words[0]) {
            return 'NC';
        }

        if (words.length === 1) {
            return words[0].substring(0, 2).toUpperCase();
        }

        return `${words[0][0]}${words[1][0]}`.toUpperCase();
    };


    const handleNameChange = (e) => {
        setName(sanitize(e.target.value));
    };

    const handleEmailChange = (e) => {
        const sanitized = sanitize(e.target.value);

        setEmail(sanitized);
        setIsValidEmail(emailValidCheck(sanitized));
    };

    const handleDesignationChange = (e) => {
        setDesignation(sanitize(e.target.value));
    };

    const handleContactNumberChange = (newNumber) => {
        setContactNumber(newNumber || '');

        setIsPossibleNumber(
            Boolean(newNumber && isPossiblePhoneNumber(newNumber))
        );

        setIsValidNumber(
            Boolean(newNumber && isValidPhoneNumber(newNumber))
        );
    };

    const handleOrganizationChange = (e, value) => {
        setOrganization(value);
    };

    const handleDepartmentChange = (e) => {
        setDepartment(sanitize(e.target.value));
    };

    const handleOrganizationNameChange = (e) => {
        setOrganizationName(sanitize(e.target.value));
    };

    const handleAddressChange = (e) => {
        setAddress(sanitize(e.target.value));
    };

    const handlePinCodeChange = (e) => {
        const value = e.target.value;
        const re = /^[0-9\b]+$/;

        if (value === '' || re.test(value)) {
            setPinCode(value);
        }
    };

    const handleCountryChange = (e, value) => {
        if (value) {
            setStates(getStates(value.code));
            setCountry(value.name);
            setCountryCode(value.code);
            setUserCountry(value);
            setUserState('');
        } else {
            setStates([]);
            setCountry('');
            setCountryCode('');
            setUserCountry(null);
            setUserState('');
        }
    };

    const handleStateChange = (e, value) => {
        setUserState(value || '');
    };

    const handleRemarksChange = (e) => {
        setRemarks(sanitize(e.target.value));
    };

    const validateImage = (file) => {
        if (!file.type.startsWith('image/')) {
            setUploadStatus('Please upload an image file.');
            return false;
        }

        const sizeLimit = 1024 * 1024;

        if (file.size > sizeLimit) {
            setUploadStatus('Image size must be less than 1MB.');
            return false;
        }

        return true;
    };

    const convertFileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.readAsDataURL(file);

            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const handleFileChange = async (e) => {
        e?.preventDefault();

        const file = e.target.files?.[0];

        if (!file) {
            return;
        }

        if (!validateImage(file)) {
            return;
        }

        try {
            const base64File = await convertFileToBase64(file);

            setPhoto(base64File);
            setImageUrl(URL.createObjectURL(file));
            setUploadStatus('');
        } catch (err) {
            setUploadStatus('Unable to process image.');
        }
    };

    const hasValidEmail = Boolean(email && isValidEmail);

    const canSubmit =
        hasValidEmail || isValidNumber;

    const handleSubmit = async (e) => {
        try {
            e?.preventDefault();

            if (!canSubmit) {
                return;
            }

            setLoading(true);

            const contactNumberObj =
                parsePhoneNumberFromString(contactNumber);

            const number =
                contactNumberObj?.nationalNumber;

            const areaCode =
                contactNumberObj?.countryCallingCode;

            const nameCardObj = {
                projectId: currentProjectId,
                name,
                email,
                organization: organization?.id || 0,
                designation,
                contactNumber: number,
                photo,
                areaCode,

                department: showAdvanced
                    ? department
                    : undefined,

                organizationName: showAdvanced
                    ? organizationName
                    : undefined,

                address: showAdvanced
                    ? address
                    : undefined,

                state: showAdvanced
                    ? userState
                    : undefined,

                country: showAdvanced
                    ? country
                    : undefined,

                countryCode: showAdvanced
                    ? countryCode
                    : undefined,

                pinCode: showAdvanced
                    ? pinCode
                    : undefined,

                remarks: showAdvanced
                    ? remarks
                    : undefined
            };

            const bodyObj = {
                nameCard: nameCardObj,
                labelId: props?.labelId
                    ? props.labelId
                    : 0
            };

            const response =
                await nameCardService.createNameCard(bodyObj);

            if (response.status < 400) {
                SnackbarManager.show(
                    response.message,
                    'success'
                );

                await props.reloadData();

                props.closePopup();
            } else {
                SnackbarManager.show(
                    response.message,
                    'error'
                );
            }
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={props.open}
            onClose={props.closePopup}
            maxWidth="md"
            fullWidth
            className={className}
            PaperProps={{
                className: `${className}__paper`
            }}
        >
            {/* HEADER */}
            <DialogTitle
                className={`${className}__header`}
            >
                <div className={`${className}__header__left`}>
                    <div className={`${className}__header__icon`}>
                        <NameCardIcon />
                    </div>

                    <div>
                        <Typography
                            className={`${className}__header__title`}
                        >
                            Create Contact
                        </Typography>

                        <Typography
                            className={`${className}__header__subtitle`}
                        >
                            Add a new contact to your project
                        </Typography>
                    </div>
                </div>

                <IconButton
                    onClick={props.closePopup}
                    className={`${className}__close`}
                    disabled={loading}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent
                className={`${className}__content`}
            >
                <form
                    onSubmit={handleSubmit}
                    id="name-card-form"
                    noValidate
                >

                    <section
                        className={`${className}__section`}
                    >
                        <div
                            className={`${className}__sectionHeader`}
                        >
                            <div
                                className={`${className}__sectionHeader__icon`}
                            >
                                <PersonOutlineIcon />
                            </div>

                            <div>
                                <Typography
                                    className={`${className}__sectionHeader__title`}
                                >
                                    Contact information
                                </Typography>

                                <Typography
                                    className={`${className}__sectionHeader__description`}
                                >
                                    Basic information used to identify and contact this person.
                                </Typography>
                            </div>
                        </div>

                        <div className={`${className}__profile`}>
                            {/* PHOTO */}
                            <div className={`${className}__photo`}>
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    id="fileInputNameCard"
                                    onChange={handleFileChange}
                                    hidden
                                />

                                <label
                                    htmlFor="fileInputNameCard"
                                    className={`${className}__photo__label`}
                                >
                                    <div
                                        className={`${className}__photo__avatar`}
                                    >
                                        <Avatar
                                            src={imageUrl}
                                            className={`${className}__avatar`}
                                        >
                                            {imageUrl
                                                ? null
                                                : getInitials(name)}
                                        </Avatar>

                                        <div
                                            className={`${className}__photo__camera`}
                                        >
                                            <PhotoCameraIcon />
                                        </div>
                                    </div>

                                    <span
                                        className={`${className}__photo__text`}
                                    >
                                        Upload photo
                                    </span>

                                    <span
                                        className={`${className}__photo__hint`}
                                    >
                                        JPG, PNG or WEBP
                                        <br />
                                        Maximum 1MB
                                    </span>
                                </label>

                                {uploadStatus && (
                                    <Alert
                                        severity="error"
                                        className={`${className}__uploadError`}
                                    >
                                        {uploadStatus}
                                    </Alert>
                                )}
                            </div>

                            {/* FIELDS */}
                            <div className={`${className}__fields`}>
                                <div className={`${className}__fieldRow`}>
                                    <TextField
                                        label="Full name"
                                        placeholder="e.g. John Doe"
                                        value={name}
                                        onChange={handleNameChange}
                                        required
                                        fullWidth
                                        inputProps={{
                                            maxLength: 200
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <PersonOutlineIcon
                                                    className={`${className}__inputIcon`}
                                                />
                                            )
                                        }}
                                    />

                                    <TextField
                                        label="Designation"
                                        placeholder="e.g. Marketing Manager"
                                        value={designation}
                                        onChange={handleDesignationChange}
                                        fullWidth
                                        inputProps={{
                                            maxLength: 200
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <WorkOutlineIcon
                                                    className={`${className}__inputIcon`}
                                                />
                                            )
                                        }}
                                    />
                                </div>

                                <div className={`${className}__fieldRow`}>
                                    <TextField
                                        label="Email address"
                                        placeholder="name@example.com"
                                        type="email"
                                        value={email}
                                        onChange={handleEmailChange}
                                        fullWidth
                                        error={
                                            Boolean(email) &&
                                            !isValidEmail
                                        }
                                        helperText={
                                            email &&
                                            !isValidEmail
                                                ? 'Please enter a valid email address'
                                                : ''
                                        }
                                        InputProps={{
                                            startAdornment: (
                                                <MailOutlineIcon
                                                    className={`${className}__inputIcon`}
                                                />
                                            )
                                        }}
                                    />

                                    <div
                                        className={`${className}__phoneWrapper`}
                                    >
                                        <label
                                            className={`${className}__phoneLabel`}
                                        >
                                            Phone number
                                        </label>

                                        <PhoneInput
                                            international
                                            defaultCountry="IN"
                                            value={contactNumber}
                                            onChange={
                                                handleContactNumberChange
                                            }
                                            className={`${className}__phoneField`}
                                        />

                                        {contactNumber &&
                                            !isPossibleNumber && (
                                                <span
                                                    className={`${className}__phoneError`}
                                                >
                                                    Enter a valid phone number
                                                </span>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <button
                        type="button"
                        className={`${className}__advancedToggle`}
                        onClick={() =>
                            setShowAdvanced(!showAdvanced)
                        }
                    >
                        <span
                            className={`${className}__advancedToggle__left`}
                        >
                            <span
                                className={`${className}__advancedToggle__icon`}
                            >
                                <LocationOnOutlinedIcon />
                            </span>

                            <span>
                                <strong>
                                    Additional details
                                </strong>

                                <small>
                                    Department, address and other information
                                </small>
                            </span>
                        </span>

                        <span
                            className={`${className}__advancedToggle__arrow`}
                        >
                            {showAdvanced
                                ? <ExpandLessIcon />
                                : <ExpandMoreIcon />}
                        </span>
                    </button>

                    {/* ADVANCED */}
                    <Collapse in={showAdvanced}>
                        <div
                            className={`${className}__advanced`}
                        >
                            {/* WORK */}
                            <div
                                className={`${className}__advancedSection`}
                            >
                                <div
                                    className={`${className}__advancedSection__title`}
                                >
                                    <BusinessOutlinedIcon />
                                    Work details
                                </div>

                                <div
                                    className={`${className}__fieldRow`}
                                >
                                    <TextField
                                        label="Organization"
                                        placeholder="e.g. KaiziQ"
                                        value={organizationName}
                                        onChange={
                                            handleOrganizationNameChange
                                        }
                                        fullWidth
                                    />
                                    <TextField
                                        label="Department"
                                        placeholder="e.g. Marketing"
                                        value={department}
                                        onChange={
                                            handleDepartmentChange
                                        }
                                        fullWidth
                                    />
                                </div>
                            </div>

                            {/* LOCATION */}
                            <div
                                className={`${className}__advancedSection`}
                            >
                                <div
                                    className={`${className}__advancedSection__title`}
                                >
                                    <LocationOnOutlinedIcon />
                                    Location
                                </div>

                                <TextField
                                    label="Address"
                                    placeholder="Enter complete address"
                                    value={address}
                                    onChange={handleAddressChange}
                                    multiline
                                    rows={3}
                                    fullWidth
                                />

                                <div
                                    className={`${className}__fieldRow`}
                                >
                                    <TextField
                                        label="PIN / ZIP code"
                                        placeholder="Enter PIN code"
                                        value={pinCode}
                                        onChange={
                                            handlePinCodeChange
                                        }
                                        fullWidth
                                        inputProps={{
                                            maxLength: 20
                                        }}
                                    />

                                    <Autocomplete
                                        options={countries}
                                        value={userCountry}
                                        onChange={
                                            handleCountryChange
                                        }
                                        getOptionLabel={
                                            (option) =>
                                                option?.name || ''
                                        }
                                        isOptionEqualToValue={
                                            (option, value) =>
                                                option?.code === value?.code
                                        }
                                        fullWidth
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Country"
                                                placeholder="Select country"
                                            />
                                        )}
                                    />

                                    <Autocomplete
                                        options={states}
                                        value={userState}
                                        onChange={
                                            handleStateChange
                                        }
                                        disabled={!userCountry}
                                        fullWidth
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="State"
                                                placeholder="Select state"
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            {/* NOTES */}
                            <div
                                className={`${className}__advancedSection`}
                            >
                                <div
                                    className={`${className}__advancedSection__title`}
                                >
                                    <NotesOutlinedIcon />
                                    Notes
                                </div>

                                <TextField
                                    label="Remarks"
                                    placeholder="Add any additional notes about this contact..."
                                    value={remarks}
                                    onChange={handleRemarksChange}
                                    multiline
                                    rows={3}
                                    fullWidth
                                />
                            </div>
                        </div>
                    </Collapse>
                </form>
            </DialogContent>

            {/* FOOTER */}
            <Divider />

            <DialogActions
                className={`${className}__footer`}
            >
                <div className={`${className}__footer__hint`}>
                    {!canSubmit && (
                        <>
                            Add a valid email or phone number
                        </>
                    )}
                </div>

                <div
                    className={`${className}__footer__actions`}
                >
                    <Button
                        onClick={props.closePopup}
                        disabled={loading}
                        className={`${className}__cancel`}
                    >
                        Cancel
                    </Button>

                    <Button
                        type="submit"
                        form="name-card-form"
                        variant="contained"
                        disabled={
                            loading ||
                            !canSubmit
                        }
                        className={`${className}__submit`}
                        startIcon={
                            loading ? (
                                <CircularProgress
                                    size={16}
                                    color="inherit"
                                />
                            ) : (
                                <NameCardIcon />
                            )
                        }
                    >
                        {loading
                            ? 'Creating...'
                            : 'Create Contact'}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
}
